import { useTranslation } from 'react-i18next';

/**
 * A column heading you can sort by.
 *
 * Three things make this behave the way people already expect from a spreadsheet:
 *
 *   - **Click sorts, click again reverses.** The first click on a new column uses that column's
 *     natural direction — newest invoice first, smallest name first — rather than always
 *     ascending, because "sort by date" almost never means "oldest first" and making people
 *     click twice to get the obvious answer is a small insult repeated all day.
 *   - **The arrow points at the truth.** `sort` and `dir` come from the *response*, not from the
 *     URL, so a heading cannot claim an ordering the server did not apply — which is exactly
 *     what happens on the first load, where the URL names no sort at all and the default is
 *     known only to the API.
 *   - **It is a button in a `th`.** `aria-sort` on the cell, a real focusable control inside it.
 *     A `th` with an onClick is invisible to a keyboard and silent to a screen reader.
 */

type Props = {
  /** The sort key the API knows this column by. */
  field: string;
  label: string;
  /** The sort actually applied, as the response reported it. */
  sort: string | undefined;
  dir: 'asc' | 'desc' | undefined;
  onSort: (field: string, dir: 'asc' | 'desc' | undefined) => void;
  /** Right-aligned, for money and counts. */
  numeric?: boolean;
  /** Fixed column width, passed through to the cell. */
  width?: number | string;
};

export function SortHeader({ field, label, sort, dir, onSort, numeric, width }: Props) {
  const { t: translate } = useTranslation();
  const t = translate as (key: string, vars?: Record<string, string | number>) => string;

  const active = sort === field;
  const direction = active ? dir ?? 'asc' : undefined;

  /*
   * Clicking the column you are already on reverses it; clicking a new one hands the direction
   * back to the API, which knows what this column's natural order is.
   */
  const next = active ? (direction === 'asc' ? 'desc' : 'asc') : undefined;

  return (
    <th
      scope="col"
      className={numeric ? 'numeric' : undefined}
      style={width === undefined ? undefined : { width }}
      aria-sort={active ? (direction === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        className={`th-sort${active ? ' th-sort--active' : ''}`}
        onClick={() => onSort(field, next)}
        // Names the action, not the state — the state is already on the cell in `aria-sort`,
        // and announcing both makes the header read as a sentence and a half.
        aria-label={t('list.sortBy', { column: label })}
      >
        <span className="label">{label}</span>
        <span className="th-sort__arrow" aria-hidden="true">
          {active ? (
            <svg width="8" height="8" viewBox="0 0 8 8">
              <path
                d={direction === 'asc' ? 'M1 5.5L4 2.5l3 3' : 'M1 2.5L4 5.5l3-3'}
                fill="none"
                stroke="currentColor"
                strokeWidth="1.4"
              />
            </svg>
          ) : (
            // A faint pair of chevrons on every sortable column, so it is discoverable that
            // the heading does anything at all without hovering each one to find out.
            <svg width="8" height="8" viewBox="0 0 8 8" className="th-sort__hint">
              <path d="M1.5 3.2L4 1l2.5 2.2M1.5 4.8L4 7l2.5-2.2" fill="none" stroke="currentColor" strokeWidth="1.1" />
            </svg>
          )}
        </span>
      </button>
    </th>
  );
}
